import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import http from 'services/http';
import Pagination from 'components/Paginacao';
import 'components/Paginacao/style.css';

/**
 * Tela de avisos da Setebit no painel da banca.
 *
 * Lista os comunicados endereçados ao usuário logado — a segmentação (banca e
 * nível) é resolvida na API, aqui só chega o que é dele. Abrir um aviso marca
 * como lido; o sino do cabeçalho é notificado pelo evento `avisos:atualizados`.
 */
const pageSize = 20; // mesmo valor do backend (paginate(20))

const formatarDataHora = (valor) => {
	if (!valor) return '—';
	// A API devolve "Y-m-d H:i:s"; o Safari não aceita esse formato com espaço.
	const data = new Date(valor.replace(' ', 'T'));
	return isNaN(data) ? '—' : data.toLocaleString('pt-BR');
};

const avisarSino = () => window.dispatchEvent(new Event('avisos:atualizados'));

export default function Avisos() {
	const [avisos, setAvisos] = useState([]);
	const [total, setTotal] = useState(0);
	const [naoLidos, setNaoLidos] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [somenteNaoLidos, setSomenteNaoLidos] = useState(false);
	const [loading, setLoading] = useState(true);
	const [aberto, setAberto] = useState(null);

	const location = useLocation();

	// O sino manda /avisos?aviso=12 — já abre esse aviso ao entrar na tela.
	const avisoDaUrl = new URLSearchParams(location.search).get('aviso');

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data: { data } } = await http.get('avisos', {
				params: {
					page: currentPage,
					nao_lidos: somenteNaoLidos ? 1 : undefined,
				},
			});

			setAvisos(data?.data ?? []);
			setTotal(data?.total ?? 0);
			setNaoLidos(data?.nao_lidos ?? 0);
		} catch (e) {
			setAvisos([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [currentPage, somenteNaoLidos]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	useEffect(() => {
		if (avisoDaUrl) setAberto(Number(avisoDaUrl));
	}, [avisoDaUrl]);

	// Abrir um aviso é o que conta como leitura — não há botão separado.
	const abrir = async (aviso) => {
		const fechando = aberto === aviso.id;
		setAberto(fechando ? null : aviso.id);

		if (fechando || aviso.lido) return;

		try {
			await http.post(`avisos/${aviso.id}/lido`);
			setAvisos((atuais) => atuais.map((item) => (
				item.id === aviso.id ? { ...item, lido: true } : item
			)));
			setNaoLidos((atual) => Math.max(0, atual - 1));
			avisarSino();
		} catch (e) {
			// falha ao marcar não impede a leitura; o próximo carregamento corrige
		}
	};

	const marcarTodos = async () => {
		try {
			await http.post('avisos/lidos');
			avisarSino();
			await carregar();
		} catch (e) {}
	};

	return (
		<div className="content">
			<div className="card">
				<div className="card-header" style={styles.cabecalho}>
					<div>
						<h4 className="card-title" style={styles.titulo}>Avisos</h4>
						<p style={styles.ajuda}>
							Comunicados da Setebit sobre o sistema. Clique no aviso para ler por inteiro.
						</p>
					</div>
					{naoLidos > 0 && (
						<button type="button" className="btn btn-sm btn-primary" onClick={marcarTodos}>
							Marcar todos como lidos ({naoLidos})
						</button>
					)}
				</div>

				<div className="card-body">
					<label style={styles.filtro}>
						<input
							type="checkbox"
							checked={somenteNaoLidos}
							onChange={(e) => { setCurrentPage(1); setSomenteNaoLidos(e.target.checked); }}
						/>
						<span style={{ marginLeft: 6 }}>Mostrar só os não lidos</span>
					</label>

					{loading ? (
						<p style={styles.vazio}>Carregando avisos...</p>
					) : avisos.length === 0 ? (
						<p style={styles.vazio}>
							{somenteNaoLidos ? 'Nenhum aviso não lido.' : 'Nenhum aviso por aqui.'}
						</p>
					) : (
						<ul style={styles.lista}>
							{avisos.map((aviso) => (
								<li
									key={aviso.id}
									style={{
										...styles.item,
										borderLeft: `3px solid ${aviso.lido ? '#e4e6eb' : '#1d6fbf'}`,
									}}
								>
									<div style={styles.itemTopo} onClick={() => abrir(aviso)}>
										<div>
											<span style={{ ...styles.itemTitulo, fontWeight: aviso.lido ? 400 : 600 }}>
												{aviso.titulo}
											</span>
											{aviso.critico && <span style={styles.tagCritico}>importante</span>}
											{!aviso.lido && <span style={styles.tagNaoLido}>não lido</span>}
										</div>
										<span style={styles.data}>{formatarDataHora(aviso.publicado_em)}</span>
									</div>

									{aberto === aviso.id ? (
										<>
											<p style={styles.mensagem}>{aviso.mensagem}</p>
											<span style={styles.rodapeItem}>
												{aviso.autor ? `Setebit · ${aviso.autor}` : 'Setebit'}
												{aviso.expira_em ? ` · fica disponível até ${formatarDataHora(aviso.expira_em)}` : ''}
											</span>
										</>
									) : (
										<p style={styles.resumo} onClick={() => abrir(aviso)}>{aviso.mensagem}</p>
									)}
								</li>
							))}
						</ul>
					)}

					<Pagination
						className="pagination-bar"
						currentPage={currentPage}
						totalCount={total}
						pageSize={pageSize}
						onPageChange={(pagina) => setCurrentPage(pagina)}
					/>
				</div>
			</div>
		</div>
	);
}

const styles = {
	cabecalho: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
	titulo: { marginBottom: 4 },
	ajuda: { margin: 0, fontSize: 13, color: '#667085' },
	filtro: { display: 'inline-flex', alignItems: 'center', fontSize: 13, color: '#344054', marginBottom: 14 },
	lista: { listStyle: 'none', margin: 0, padding: 0 },
	item: {
		background: '#fff', border: '1px solid #e4e6eb', borderRadius: 8,
		padding: '12px 16px', marginBottom: 10,
	},
	itemTopo: {
		display: 'flex', justifyContent: 'space-between', alignItems: 'center',
		gap: 10, cursor: 'pointer', flexWrap: 'wrap',
	},
	itemTitulo: { fontSize: 15, color: '#1f2328' },
	tagCritico: {
		background: '#fce8ea', color: '#b32233', borderRadius: 4, marginLeft: 8,
		fontSize: 10, padding: '2px 6px', textTransform: 'uppercase',
	},
	tagNaoLido: {
		background: '#e7f1fd', color: '#1d6fbf', borderRadius: 4, marginLeft: 6,
		fontSize: 10, padding: '2px 6px', textTransform: 'uppercase',
	},
	data: { fontSize: 12, color: '#98a2b3' },
	resumo: {
		display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
		overflow: 'hidden', fontSize: 13, color: '#667085', margin: '6px 0 0', cursor: 'pointer',
	},
	mensagem: {
		fontSize: 14, lineHeight: 1.55, color: '#344054',
		whiteSpace: 'pre-wrap', margin: '10px 0 0',
	},
	rodapeItem: { display: 'block', fontSize: 12, color: '#98a2b3', marginTop: 10 },
	vazio: { fontSize: 14, color: '#667085' },
};
